import type { CommandPaletteCategory, CommandPaletteItem } from "@design";
import { useQuery } from "@tanstack/react-query";
import {
  Bot,
  CircleHelp,
  Folder,
  House,
  Lock,
  MessageCircle,
  MessageSquarePlus,
  Settings,
  Sparkle,
  Wrench,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { chatListSessionsOptions, listAgentsOptions, listSpacesOptions } from "@/api";
import { useFeedbackDialog } from "@/features/feedback/FeedbackProvider";
import { useHelpArticles } from "@/features/help/useHelpArticles";
import { DESKTOP_ONLY, useAvailableOn } from "@/responsive/surfaces";

/** What the palette can jump to: recent chats, spaces, assistants, and app actions. */
export function useCommandCategories(): CommandPaletteCategory[] {
  const { t } = useTranslation("shell", { keyPrefix: "palette" });
  const navigate = useNavigate();
  const buildAvailable = useAvailableOn(DESKTOP_ONLY);
  const feedback = useFeedbackDialog();
  const chats = useQuery({ ...chatListSessionsOptions({ query: { page_size: 20 } }), select: (r) => r.data });
  const spaces = useQuery({ ...listSpacesOptions({ query: { page_size: 100 } }), select: (r) => r.data });
  const agents = useQuery({ ...listAgentsOptions(), select: (r) => r.data });

  const chatItems: CommandPaletteItem[] = (chats.data ?? []).map((chat) => ({
    value: `chat:${chat.uuid}`,
    icon: <MessageCircle size={16} />,
    label: chat.title || t("untitledChat"),
    hint: t("hints.chat"),
    onSelect: () => navigate(`/chat/${chat.uuid}`),
  }));
  const spaceItems: CommandPaletteItem[] = (spaces.data ?? []).map((space) => ({
    value: `space:${space.uuid}`,
    icon: space.isPersonal ? <Lock size={16} /> : <Folder size={16} />,
    label: space.name,
    hint: t("hints.space"),
    onSelect: () => navigate(`/spaces/${space.uuid}`),
  }));
  const agentItems: CommandPaletteItem[] = (agents.data ?? []).map((agent) => ({
    value: `agent:${agent.uuid}`,
    icon: <Bot size={16} />,
    label: agent.name,
    hint: t("hints.agent"),
    onSelect: () => navigate(`/chat?agent=${agent.uuid}`),
  }));
  const helpItems: CommandPaletteItem[] = useHelpArticles().map((article) => ({
    value: `help:${article.slug}`,
    icon: <CircleHelp size={16} />,
    label: article.title,
    hint: t("hints.help"),
    onSelect: () => navigate(`/help/${article.slug}`),
  }));
  const navItems: CommandPaletteItem[] = [
    { value: "action:new-chat", icon: <MessageCircle size={16} />, label: t("actions.newChat"), to: "/chat" },
    { value: "action:chats", icon: <MessageCircle size={16} />, label: t("actions.chats"), to: "/chats" },
    { value: "action:home", icon: <House size={16} />, label: t("actions.home"), to: "/" },
    { value: "action:spaces", icon: <Folder size={16} />, label: t("actions.spaces"), to: "/spaces" },
    ...(buildAvailable
      ? [{ value: "action:build", icon: <Wrench size={16} />, label: t("actions.build"), to: "/build" }]
      : []),
    { value: "action:settings", icon: <Settings size={16} />, label: t("actions.settings"), to: "/settings" },
    { value: "action:help", icon: <CircleHelp size={16} />, label: t("actions.help"), to: "/help" },
  ].map(({ to, ...item }) => ({ ...item, hint: t("hints.action"), onSelect: () => navigate(to) }));
  const actionItems: CommandPaletteItem[] = [
    ...navItems,
    {
      value: "action:feedback",
      icon: <MessageSquarePlus size={16} />,
      label: t("actions.feedback"),
      hint: t("hints.dialog"),
      onSelect: feedback.open,
    },
  ];

  return [
    { value: "chats", label: t("categories.chats"), icon: <MessageCircle size={16} />, items: chatItems },
    { value: "spaces", label: t("categories.spaces"), icon: <Folder size={16} />, items: spaceItems },
    { value: "assistants", label: t("categories.assistants"), icon: <Bot size={16} />, items: agentItems },
    { value: "actions", label: t("categories.actions"), icon: <Sparkle size={16} />, items: actionItems },
    { value: "help", label: t("categories.help"), icon: <CircleHelp size={16} />, items: helpItems },
  ];
}
